"use strict";
// CalendarNavigation.ts
Object.defineProperty(exports, "__esModule", { value: true });
exports.CalendarNavigation = exports.CalendarNavigationElement = void 0;
const ButtonToggle_1 = require("../ButtonToggle/ButtonToggle");
const DatePicker_1 = require("../DatePicker/DatePicker");
const SelectInput_1 = require("../SelectInput/SelectInput");
const InlineSelect_1 = require("../InlineSelect/InlineSelect");
const web_components_1 = require("../web-components");
const sw_constants_1 = require("../../../modules/sw-constants");
const CalendarNavigationStyles_1 = require("./CalendarNavigationStyles");
class CalendarNavigationElement extends HTMLElement {
    shadowRoot; // TODO: Might be easier to remove
    updateCalendarSettings;
    day_string = this.getDayStringAttribute;
    calendar_view = this.getCalendarViewAttribute;
    week_start = this.getWeekStartAttribute;
    selected_track = "";
    tracks = [];
    programs = [];
    selected_program = "";
    // TODO: Over rending need to use these cached references instead of querying DOM each time
    // Cache DOM references for performance
    weekStartSelect;
    viewAsRoleSelect;
    showAnnouncementsCheckbox;
    showDescriptionsCheckbox;
    buttonToggle;
    trackSelect;
    programSelect;
    prevButton;
    prevDayButton;
    nextButton;
    nextDayButton;
    todayButton;
    // Render State tracking
    hasRendered = false;
    previousAttributes = new Map();
    static get observedAttributes() {
        return [
            "day_string",
            "selected_track",
            "tracks",
            "programs",
            "selected_program",
            "calendar_view",
            "week_start",
            "view_as_roles",
            "selected_view_as_role",
            "show_descriptions",
            "show_announcements_and_photo",
            "callback-key",
        ];
    }
    constructor() {
        super(); // Makes <calendar-navigation> element a valid HTMLElement
        this.shadowRoot = (0, web_components_1.safeAttachShadow)(this, "open"); // Only attach if not already attached by monkey-patch fixes MutationObserver issue.
    }
    // Invoked when the custom element is appended to the document's DOM.
    connectedCallback() {
        // Following instruction #16 - render once on mount
        if (!this.hasRendered) {
            this.setupCallbackFromRegistry();
            this.render();
            this.hasRendered = true;
        }
    }
    // Invoked when one of the custom element's attributes is added, removed, or changed.
    attributeChangedCallback(name, oldValue, newValue) {
        // Browser provides oldValue, no need to track
        if (!this.hasRendered) {
            // this.previousAttributes.set(name, newValue);
            return; // Skip during construction
        }
        if (oldValue === newValue)
            return; // No actual change
        // Selective updates instead of full re-render
        this.render();
        // this.updateDOM(name, newValue);
    }
    disconnectedCallback() {
        // Cleanup to prevent memory leaks
        const callbackKey = this.getAttribute("callback-key");
        if (callbackKey && window.__calendarNavigationCallbacks) {
            window.__calendarNavigationCallbacks.delete(callbackKey);
        }
        // Clear cached references
        this.clearDOMReferences();
    }
    clearDOMReferences() {
        this.weekStartSelect = undefined;
        this.viewAsRoleSelect = undefined;
        this.showAnnouncementsCheckbox = undefined;
        this.showDescriptionsCheckbox = undefined;
        this.buttonToggle = undefined;
        this.trackSelect = undefined;
        this.programSelect = undefined;
        this.prevButton = undefined;
        this.prevDayButton = undefined;
        this.nextButton = undefined;
        this.nextDayButton = undefined;
        this.todayButton = undefined;
    }
    // Attribute Getters
    get getViewAsRoles() {
        const view_as_roles = JSON.parse(this.getAttribute("view_as_roles") || "null");
        if (Array.isArray(view_as_roles)) {
            return view_as_roles;
        }
        return [];
    }
    get getCalendarViewAttribute() {
        this.calendar_view = (this.getAttribute("calendar_view") || "week").toLowerCase();
        return this.calendar_view;
    }
    get getDayStringAttribute() {
        return this.getAttribute("day_string") || this.getDayStringAsYYYYMMDD();
    }
    get getWeekStartAttribute() {
        return ((0, sw_constants_1.parseWeekDayIndex)(this.getAttribute("week_start")) ??
            sw_constants_1.DEFAULT_WEEK_START);
    }
    get getShowDescriptionsAttribute() {
        const show_descriptions = this.getAttribute("show_descriptions") || "false";
        return show_descriptions.toLocaleLowerCase() === "true";
    }
    get getShowAnnouncementsAndPhotoAttribute() {
        const show_announcements_and_photo = this.getAttribute("show_announcements_and_photo") || "false";
        return show_announcements_and_photo.toLocaleLowerCase() === "true";
    }
    // Retrieves and sets the updateCalendarSettings callback from the global registry minimizes global namespace pollution.
    setupCallbackFromRegistry() {
        const callbackKey = this.getAttribute("callback-key");
        if (!callbackKey || !window.__calendarNavigationCallbacks)
            return;
        const callback = window.__calendarNavigationCallbacks.get(callbackKey);
        if (callback)
            this.updateCalendarSettings = callback;
    }
    triggerSettingsUpdate(updates) {
        if (!this.updateCalendarSettings) {
            console.warn("[CalendarNavigation] No updateCalendarSettings callback available");
            return;
        }
        this.updateCalendarSettings(updates);
    }
    // Utility Methods for date manipulation and formatting
    getDayStringAsYYYYMMDD(date = new Date()) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0"); // Month is 0-indexed
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}${month}${day}`;
    }
    parseDateFromDayString(dayString // YYYYMMDD
    ) {
        const year = parseInt(dayString.slice(0, 4));
        const month = parseInt(dayString.slice(4, 6)) - 1; // Month is 0-indexed
        const day = parseInt(dayString.slice(6, 8));
        if (year < 1900 ||
            year > 2100 ||
            month < 0 ||
            month > 11 ||
            day < 1 ||
            day > 31) {
            console.warn(`Invalid date components in ${dayString}`);
            return null;
        }
        return { year, month, day };
    }
    getWeekStart(date, startWeekOn) {
        const dayOfWeek = date.getDay(); // 0 = Sunday ... 6 = Saturday
        const weekStartDay = (0, sw_constants_1.parseWeekDayIndex)(startWeekOn) ?? sw_constants_1.DEFAULT_WEEK_START;
        const weekStart = new Date(date);
        const daysToSubtract = (dayOfWeek - weekStartDay + 7) % 7;
        weekStart.setDate(date.getDate() - daysToSubtract);
        return weekStart;
    }
    getFormattedDateString(day_string, // YYYYMMDD
    start_week_on = sw_constants_1.DEFAULT_WEEK_START, calendar_view) {
        const parsedDate = this.parseDateFromDayString(day_string);
        if (!parsedDate)
            return "Invalid Day String";
        const { year, month, day } = parsedDate;
        const givenDate = new Date(year, month, day);
        if (isNaN(givenDate.getTime())) {
            console.warn(`Invalid date created from ${day_string}`);
            return "Invalid Day String";
        }
        if (!calendar_view) {
            // Default to day view
            // Format: "Dec 03, 2025"
            return givenDate.toLocaleDateString("en-US", {
                month: "short",
                day: "2-digit",
                year: "numeric",
            });
        }
        if (calendar_view === "week") {
            // Calculate proper week boundaries
            const weekStart = this.getWeekStart(givenDate, start_week_on);
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 6);
            const startMonth = weekStart.toLocaleDateString("en-US", {
                month: "short",
            });
            const startDay = weekStart.toLocaleDateString("en-US", {
                day: "2-digit",
            });
            // Check if week spans multiple months or years
            const endMonth = weekEnd.toLocaleDateString("en-US", {
                month: "short",
            });
            const endDay = weekEnd.toLocaleDateString("en-US", {
                day: "2-digit",
            });
            const endYear = weekEnd.getFullYear();
            const startYear = weekStart.getFullYear();
            // Format: "Nov 30 - Dec 06, 2025" or "Dec 26, 2025 - Jan 01, 2026"
            if (startYear !== endYear) {
                // Week spans multiple years
                return `${startMonth} ${startDay}, ${startYear} - ${endMonth} ${endDay}, ${endYear}`;
            }
            else if (startMonth !== endMonth) {
                // Week spans multiple months in same year
                return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${endYear}`;
            }
            else {
                // Week is within same month
                return `${startMonth} ${startDay} - ${endDay}, ${endYear}`;
            }
        }
        if (calendar_view === "month") {
            // Format: "December, 2025"
            return givenDate.toLocaleDateString("en-US", {
                month: "long",
                year: "numeric",
            });
        }
    }
    /**
     * Returns formatted date text based on view mode
     * @param day_string - Date string in YYYYMMDD format (e.g., "20251203")
     * @param calendar_view - View mode: "Day", "Week", or "Month"
     * @param start_week_on - Week start day index: 0=Sunday ... 6=Saturday
     * @returns Formatted date string:
     *   - Day: "Dec 03, 2025"
     *   - Week: "Nov 30 - Dec 06, 2025" (if Sunday) | "Dec 01 - Dec 07, 2025" (if Monday)
     *   - Month: "December, 2025"
     */
    getDateText(day_string, start_week_on = sw_constants_1.DEFAULT_WEEK_START, calendar_view) {
        // Early validation
        if (!day_string || typeof day_string !== "string") {
            console.warn("[CalendarNavigation] Invalid date: must be a non-empty string");
            return this.day_string;
        }
        // Validate YYYYMMDD format
        if (day_string.length !== 8 || !/^\d{8}$/.test(day_string)) {
            console.warn(`Invalid date format: ${day_string}. Expected YYYYMMDD format.`);
            return "Invalid Date Range";
        }
        const formattedDate = this.getFormattedDateString(day_string, start_week_on, calendar_view ?? undefined);
        return calendar_view === "week"
            ? `Week: ${formattedDate}`
            : formattedDate;
    }
    convertDayStringToISO(dayString) {
        if (!dayString || dayString.length !== 8) {
            console.warn(`[CalendarNavigation] Invalid dayString: ${dayString}`);
            return "";
        }
        const year = dayString.slice(0, 4);
        const month = dayString.slice(4, 6);
        const day = dayString.slice(6, 8);
        return `${year}-${month}-${day}`;
    }
    convertISOToDayString(isoDate) {
        if (!isoDate) {
            console.warn(`[CalendarNavigation] Invalid isoDate: ${isoDate}`);
            return this.getDayStringAsYYYYMMDD();
        }
        // Remove hyphens: "2025-12-19" -> "20251219"
        return isoDate.replace(/-/g, "");
    }
    getAdjacentDate(direction, useViewSelected = false) {
        let viewSelected = "week";
        let parsedDate;
        const currentDayString = this.getDayStringAttribute;
        if (useViewSelected)
            viewSelected = this.getCalendarViewAttribute;
        try {
            parsedDate = this.parseDateFromDayString(currentDayString);
            if (!parsedDate)
                return currentDayString;
        }
        catch (e) {
            console.error("[CalendarNavigation] Error parsing date from day string:", e);
        }
        const date = new Date(parsedDate.year, parsedDate.month, parsedDate.day);
        if (!useViewSelected) {
            // Default to day
            date.setDate(date.getDate() + direction);
        }
        else if (useViewSelected && viewSelected === "week") {
            date.setDate(date.getDate() + 7 * direction);
        }
        else if (useViewSelected && viewSelected === "month") {
            date.setMonth(date.getMonth() + direction);
        }
        return this.getDayStringAsYYYYMMDD(date);
    }
    updateURL(updates) {
        // Get current URL params
        const url = new URL(window.location.href);
        const params = url.searchParams;
        // Apply updates
        if (updates.track !== undefined) {
            if (updates.track)
                params.set("track", updates.track);
            else
                params.delete("track");
        }
        if (updates.week !== undefined) {
            if (updates.week)
                params.set("week", updates.week);
            else
                params.delete("week");
        }
        if (updates.view !== undefined) {
            if (updates.view)
                params.set("view", updates.view);
            else
                params.delete("view");
        }
        if (updates.program !== undefined) {
            if (updates.program) {
                params.set("program", updates.program);
            }
            else {
                params.delete("program");
            }
        }
        // Update URL without reload
        const newURL = `${url.pathname}?${params.toString()}`;
        window.history.pushState({}, "", newURL);
        console.log(`[CalendarNavigation] URL updated to: ${newURL}`);
        // Dispatch a custom event for other components to react
        this.dispatchEvent(new CustomEvent("url-changed", {
            bubbles: true,
            composed: true,
            detail: {
                track: params.get("track"),
                week: params.get("week"),
                view: params.get("view"),
                program: params.get("program"),
            },
        }));
    }
    getTracksSelectInputAttribute() {
        const selected_track = this.getAttribute("selected_track") || "";
        const tracks_attribute = this.getAttribute("tracks") || "[]";
        let tracks = [];
        try {
            const decodedTracks = (0, web_components_1.decodeAttributeStringBeforeJSONParse)(tracks_attribute);
            tracks = JSON.parse(decodedTracks);
        }
        catch (error) {
            console.error("Error parsing tracks attribute:", error);
            tracks = [];
        }
        console.log("[CalendarNavigation] - Tracks parsed for SelectInput:");
        console.log(tracks);
        const tracksAttributes = {
            label: "Track",
            name: "track-select",
            value: selected_track,
            options: tracks.map((track) => {
                let trackGroup;
                // Order: Templates → Private Group → Public → Default (ungrouped)
                if (track.isTemplate)
                    trackGroup = "Templates";
                else if (track.isPrivateGroup)
                    trackGroup = "Private Group Tracks";
                else if (track.isPublic)
                    trackGroup = "Public Group Tracks";
                else
                    trackGroup = undefined; // No group (appears at top)
                return {
                    label: track.name,
                    value: track.key,
                    group: trackGroup,
                };
            }) || [],
        };
        console.log("[CalendarNavigation] - Tracks SelectInput attributes:");
        console.log(tracksAttributes);
        return tracksAttributes;
    }
    getProgramsSelectInputAttribute() {
        const selected_program = this.getAttribute("selected_program") || "";
        const programs_attribute = this.getAttribute("programs") || "[]";
        let programs = [];
        try {
            const decodedPrograms = (0, web_components_1.decodeAttributeStringBeforeJSONParse)(programs_attribute);
            programs = JSON.parse(decodedPrograms);
        }
        catch (error) {
            console.error("Error parsing programs attribute:", error);
            programs = [];
        }
        console.log("[CalendarNavigation] - Programs parsed for SelectInput:");
        console.log(programs);
        const mappedPrograms = programs
            .map((program) => {
            // spread the returned mapped array
            return program.publishingTracks.map((track) => ({
                label: `${program.name} - ${track.name}`,
                value: track.key,
                group: program.name, // Using the flat grouping key
            }));
        })
            .flat() || [];
        console.log("[CalendarNavigation] - Mapped Programs for SelectInput options:");
        console.log(mappedPrograms);
        const programsAttributes = {
            label: "Available Programs",
            name: "program-select",
            value: selected_program,
            options: mappedPrograms || [],
        };
        return programsAttributes;
    }
    getViewAsRolesInlineSelectInputAttributes() {
        const view_as_roles = this.getViewAsRoles;
        const selected_view_as_role = this.getAttribute("selected_view_as_role") || "coach";
        const viewPermissionsAttributes = {
            label: "View As",
            name: "view-as-role-select",
            value: selected_view_as_role,
            options: view_as_roles.map((role) => ({
                label: role.name,
                value: role.whiteboardRoleKey,
                shortLabel: role.name,
            })),
        };
        return viewPermissionsAttributes;
    }
    getStartWeekInlineSelectInputAttributes() {
        const week_start = this.getWeekStartAttribute;
        const startWeekAttributes = {
            label: "Week Starts On",
            name: "start-week-select",
            value: String(week_start),
            options: [
                {
                    label: "Sunday",
                    value: String(sw_constants_1.WEEK_DAY_INDEX.SUNDAY),
                    shortLabel: "Sun",
                },
                {
                    label: "Monday",
                    value: String(sw_constants_1.WEEK_DAY_INDEX.MONDAY),
                    shortLabel: "Mon",
                },
            ],
        };
        return startWeekAttributes;
    }
    getCalendarViewButtonAttributes() {
        const calendar_view = this.getAttribute("calendar_view") || "week";
        const buttonToggleAttributes = {
            name: "calendar-view",
            value: calendar_view,
            options: [
                { value: "week", label: "Week" },
                { value: "month", label: "Month" },
            ],
        };
        return buttonToggleAttributes;
    }
    getDatePickerAttributes() {
        const day_string = this.getDayStringAttribute; // YYYYMMDD
        const datePickerAttributes = {
            id: "calendar-navigation-date-picker",
            name: "calendar-navigation-date-picker",
            value: this.convertDayStringToISO(day_string), // convert YYYYMMDD to YYYY-MM-DD for HTML5 date input
        };
        return datePickerAttributes;
    }
    render() {
        console.log("render(): [CalendarNavigation] ---------------");
        const calendar_view = this.getCalendarViewAttribute;
        const week_start = this.getWeekStartAttribute;
        const day_string = this.getDayStringAttribute; // YYYYMMDD
        const datePickerAttributes = this.getDatePickerAttributes();
        const tracksSelectInputAttributes = this.getTracksSelectInputAttribute();
        const programsSelectInputAttribute = this.getProgramsSelectInputAttribute();
        const startWeekInlineSelectInputAttributes = this.getStartWeekInlineSelectInputAttributes();
        const viewAsRolesInlineSelectInputAttributes = this.getViewAsRolesInlineSelectInputAttributes();
        const calendarViewButtonAttributes = this.getCalendarViewButtonAttributes();
        const showDescriptions = this.getShowDescriptionsAttribute;
        const showAnnouncements = this.getShowAnnouncementsAndPhotoAttribute;
        console.log("[CalendarNavigation] Render state:", {
            showDescriptions,
            showAnnouncements,
            calendar_view,
            day_string,
            tracksSelectInputAttributes,
            programsSelectInputAttribute,
            startWeekInlineSelectInputAttributes,
            viewAsRolesInlineSelectInputAttributes,
            calendarViewButtonAttributes,
        });
        this.shadowRoot.innerHTML = `
            <style>
                /* Dreamcoat Web Component part selector override */
                :host {
                   width: 100%;
                }

                .CalendarNavigation__actionButton.copy::part(button) {
                   ${CalendarNavigationStyles_1.styles.actionButtonCopy}
                }
                .CalendarNavigation__actionButton.download::part(button) {
                   ${CalendarNavigationStyles_1.styles.actionButtonDownload}
                }
                .CalendarNavigation__actionButton.delete::part(button) {
                   ${CalendarNavigationStyles_1.styles.actionButtonDelete}
                }
            </style>
            <nav class="dreamcoat CalendarNavigation" part="CalendarNavigation">
                <div class="CalendarNavigation__dateNavigation" part="date-navigation">  
                    <div class="CalendarNavigation__dateControls" part="dateControls">
                        <dc-button 
                            class="CalendarNavigation__previousButton" 
                            part="previousButton" type="button" 
                            icon-left-family="regular" 
                            icon-left="chevron-left" 
                            icon-only="true" 
                            label="Button" 
                            size="md" 
                            variant="tertiary-primary">
                        </dc-button>
                        <dc-button 
                            class="CalendarNavigation__previousButton--day" 
                            part="previousButtonDay" type="button" 
                            icon-left-family="regular" 
                            icon-left="chevron-left" 
                            icon-only="true" 
                            label="Button" 
                            size="md" 
                            variant="tertiary-primary">
                        </dc-button>
                        <span class="CalendarNavigation__dateText body-L" part="dateText">
                            ${this.getDateText(day_string, week_start, calendar_view)}
                        </span>
                        <span class="CalendarNavigation__dateText--day body-L" part="dateText--day">
                            ${this.getDateText(day_string, week_start)}
                        </span>
                        <dc-button 
                            class="CalendarNavigation__nextButton--day" 
                            part="nextButtonDay" type="button" 
                            icon-right-family="regular" 
                            icon-right="chevron-right" 
                            icon-only="true" 
                            label="Button" 
                            size="md" 
                            variant="tertiary-primary">
                        </dc-button>
                        <dc-button 
                            class="CalendarNavigation__nextButton" 
                            part="nextButton" type="button" 
                            icon-right-family="regular" 
                            icon-right="chevron-right" 
                            icon-only="true" 
                            label="Button" 
                            size="md" 
                            variant="tertiary-primary">
                        </dc-button>
                    </div>
                    
                    ${(0, DatePicker_1.DatePicker)({ attributes: datePickerAttributes })}

                    <dc-button class="CalendarNavigation__todayButton" part="todayButton" type="button" label="Today" size="md" variant="tertiary-primary"></dc-button>
                    <div class="CalendarNavigation__viewButtons" part="viewButtons">
                        ${(0, ButtonToggle_1.ButtonToggle)({
            attributes: calendarViewButtonAttributes,
        })}
                    </div>
                    </div>
                </div>
        
                <div class="CalendarNavigation__selectButtons" part="selectButtons">
                    ${(0, SelectInput_1.SelectInput)({ attributes: tracksSelectInputAttributes })}
                    ${(0, SelectInput_1.SelectInput)({ attributes: programsSelectInputAttribute })}
                </div>
            </nav>

            <div class="CalendarNavigation__footer" part="footer">
                <div class="CalendarNavigation__viewControls" part="viewControls">
                    ${(0, InlineSelect_1.InlineSelect)({
            attributes: startWeekInlineSelectInputAttributes,
        })}
                    ${(0, InlineSelect_1.InlineSelect)({
            attributes: viewAsRolesInlineSelectInputAttributes,
        })}
                </div>

                <div class="CalendarNavigation__actionsControls" part="actionsControls">
                    <dc-checkbox 
                        class="CalendarNavigation__actionCheckbox" 
                        part="actionCheckbox" 
                        id="show-announcements-checkbox" 
                        label="Show Announcements + Photo" 
                        name="show-announcements" 
                        value="show-announcements"
                        ${showAnnouncements ? "checked" : ""}
                    ></dc-checkbox>
                    <dc-checkbox 
                        class="CalendarNavigation__actionCheckbox" 
                        part="actionCheckbox" 
                        id="show-descriptions-checkbox" 
                        label="Show Workout Descriptions" 
                        name="show-descriptions" 
                        value="show-descriptions"
                        ${showDescriptions ? "checked" : ""}
                    ></dc-checkbox>

                    <dc-button-group class="CalendarNavigation__actionButtonGroup" part="actionButtonGroup" group-aria-label="Action Buttons" >
                        <dc-button style="border-radius: 0px;" class="CalendarNavigation__actionButton copy" part="actionButton" variant="tertiary-primary" size="md" icon-only="true" icon-left-family="regular" icon-left="copy" icon-right-family="regular"></dc-button>
                        <dc-button style="border-radius: 0px;" class="CalendarNavigation__actionButton download" part="actionButton" variant="tertiary-primary" size="md" icon-only="true" icon-left-family="regular" icon-left="download" icon-right-family="regular"></dc-button>
                        <dc-button style="border-radius: 0px;" class="CalendarNavigation__actionButton delete" part="actionButton" variant="tertiary-primary" size="md" icon-only="true" icon-left-family="regular" icon-left="trash" icon-right-family="regular"></dc-button>
                    </dc-button-group>

                    <dc-button class="CalendarNavigation__actionsMenu" part="actionsMenu" icon-left="gear" icon-only="true"  type="button" size="md" variant="tertiary-primary"></dc-button>
                    <dc-button class="CalendarNavigation__add" part="addButton" type="button" icon-left="plus" label="ADD" size="md" variant="primary"></dc-button>
                </div>  
            </div>
        `;
        this.attachEventListeners();
    }
    // Event Listeners
    attachEventListeners() {
        // DatePicker change listener
        const datePicker = this.shadowRoot.querySelector("date-picker");
        if (datePicker) {
            // Listen for 'change' not 'input' to avoid triggering on month navigation
            datePicker.addEventListener("change", this.onDatePickerChange);
        }
        else {
            console.warn("[CalendarNavigation] DatePicker element not found in shadow DOM");
        }
        // Week start toggle
        const weekStartSelect = this.shadowRoot.querySelector('inline-select[name="start-week-select"]');
        if (weekStartSelect)
            weekStartSelect.addEventListener("change", this.onWeekStartChange);
        // View as role toggle
        const viewAsRoleSelect = this.shadowRoot.querySelector('inline-select[name="view-as-role-select"]');
        if (viewAsRoleSelect) {
            viewAsRoleSelect.addEventListener("change", (event) => {
                const target = event.target;
                const newRole = target.value;
                // Update local attribute
                this.setAttribute("selected_view_as_role", newRole);
                // Trigger parent update
                this.triggerSettingsUpdate({
                    selected_view_as_role: newRole,
                    view_mode: newRole, // Update legacy key too
                });
            });
        }
        // Show announcements checkbox
        const showAnnouncementsCheckbox = this.shadowRoot.querySelector("#show-announcements-checkbox");
        if (showAnnouncementsCheckbox) {
            showAnnouncementsCheckbox.addEventListener("change", (event) => {
                // event.target is the dc-checkbox element itself
                const dreamcoatCheckbox = event.target?.checkbox;
                const isChecked = dreamcoatCheckbox?.checked ?? false;
                // Update local attribute
                this.setAttribute("show_announcements_and_photo", String(isChecked));
                // Trigger parent update
                this.triggerSettingsUpdate({
                    show_announcements_and_photo: isChecked,
                    show_whiteboards: isChecked, // Update legacy key too
                });
            });
        }
        // Show descriptions checkbox
        const showDescriptionsCheckbox = this.shadowRoot.querySelector("#show-descriptions-checkbox");
        if (showDescriptionsCheckbox) {
            showDescriptionsCheckbox.addEventListener("change", (event) => {
                // event.target is the dc-checkbox element itself
                const dreamcoatCheckbox = event.target?.checkbox;
                const isChecked = dreamcoatCheckbox?.checked ?? false;
                // Update local attribute
                this.setAttribute("show_descriptions", String(isChecked));
                // Trigger parent update
                this.triggerSettingsUpdate({
                    show_descriptions: isChecked,
                });
            });
        }
        // Calendar view toggle (Week/Month)
        const calendarViewToggle = this.shadowRoot.querySelector("button-toggle");
        if (calendarViewToggle) {
            calendarViewToggle.addEventListener("change", this.onCalendarViewChange);
        }
        else {
            console.warn("[CalendarNavigation] calendarViewToggle element not found in shadow DOM");
        }
        // Track select dropdown
        const trackSelect = this.shadowRoot.querySelector('select-input[name="track-select"]');
        if (trackSelect)
            trackSelect.addEventListener("change", this.onTracksChanged);
        // Program select dropdown (if needed)
        const programSelect = this.shadowRoot.querySelector('select-input[name="program-select"]');
        if (programSelect) {
            programSelect.addEventListener("change", (event) => {
                const target = event.target;
                const newProgramId = target.value;
                console.log(`[CalendarNavigation] Program changed to: ${newProgramId}`);
                // this.updateURL({ program: newProgramId });
            });
        }
        // Previous/Next date navigation
        const prevButton = this.shadowRoot.querySelector(".CalendarNavigation__previousButton");
        const prevDayButton = this.shadowRoot.querySelector(".CalendarNavigation__previousButton--day");
        const nextButton = this.shadowRoot.querySelector(".CalendarNavigation__nextButton");
        const nextDayButton = this.shadowRoot.querySelector(".CalendarNavigation__nextButton--day");
        const todayButton = this.shadowRoot.querySelector(".CalendarNavigation__todayButton");
        if (prevButton) {
            prevButton.addEventListener("click", () => {
                const newDate = this.getAdjacentDate(-1, true);
                this.setAttribute("day_string", newDate);
                this.updateURL({ week: newDate });
            });
        }
        if (prevDayButton) {
            prevDayButton.addEventListener("click", () => {
                const newDate = this.getAdjacentDate(-1, false);
                this.setAttribute("day_string", newDate);
                this.updateURL({ week: newDate });
            });
        }
        if (nextButton) {
            nextButton.addEventListener("click", () => {
                const newDate = this.getAdjacentDate(1, true);
                this.setAttribute("day_string", newDate);
                this.updateURL({ week: newDate });
            });
        }
        if (nextDayButton) {
            nextDayButton.addEventListener("click", () => {
                const newDate = this.getAdjacentDate(1, false);
                this.setAttribute("day_string", newDate);
                this.updateURL({ week: newDate });
            });
        }
        if (todayButton) {
            todayButton.addEventListener("click", () => {
                const today = this.getDayStringAsYYYYMMDD();
                this.setAttribute("day_string", today);
                this.updateURL({ week: today });
            });
        }
    }
    onDatePickerChange = (event) => {
        const target = event.target;
        const newISODate = target?.value;
        let dayString;
        if (!newISODate)
            dayString = this.getDayStringAsYYYYMMDD(); // Fallback to today
        else
            dayString = this.convertISOToDayString(newISODate);
        // Update local attribute
        this.setAttribute("day_string", dayString);
        // Update URL
        this.updateURL({ week: dayString });
    };
    onWeekStartChange = (event) => {
        const target = event.target;
        const newWeekStart = (0, sw_constants_1.parseWeekDayIndex)(target.value) ?? sw_constants_1.DEFAULT_WEEK_START;
        // Update local attribute
        this.setAttribute("week_start", String(newWeekStart));
        // Trigger parent update
        this.triggerSettingsUpdate({ week_start: newWeekStart });
    };
    onTracksChanged = (event) => {
        const target = event.target;
        const newTrackKey = target.value;
        // Update attribute
        this.setAttribute("selected_track", newTrackKey);
        // Update URL
        this.updateURL({ track: newTrackKey });
        // Trigger parent update
        this.triggerSettingsUpdate({ trackId: newTrackKey });
    };
    onCalendarViewChange = (event) => {
        const customEvent = event;
        const newValue = customEvent.detail?.value;
        if (!newValue) {
            console.warn("[CalendarNavigation] calendarViewToggle change event has no value");
            return;
        }
        const calendarView = newValue;
        // Prevent unnecessary re-render
        // Only update if value actually changed
        if (this.getCalendarViewAttribute === calendarView)
            return;
        // Update local attribute (triggers attributeChangedCallback)
        this.setAttribute("calendar_view", calendarView);
        // Trigger parent update
        this.triggerSettingsUpdate({ calendar_view: calendarView });
    };
}
exports.CalendarNavigationElement = CalendarNavigationElement;
const CalendarNavigation = (props) => {
    console.log("[CalendarNavigation] - [props] passed into helper function:");
    console.log(props);
    const { day_string, calendar_view, selected_track, tracks, programs, view_as_roles, show_descriptions, show_announcements_and_photo, week_start, selected_view_as_role, updateCalendarSettings, } = props;
    const id = `calendar-navigation-${Date.now()}`; // Unique ID for the element instance
    const tracksAsEscapedString = (0, web_components_1.stringyAndEscapeCharsForHTMLAttribute)(tracks || []);
    const viewAsRolesAsEscapedString = (0, web_components_1.stringyAndEscapeCharsForHTMLAttribute)(view_as_roles || []);
    const programsAsEscapedString = (0, web_components_1.stringyAndEscapeCharsForHTMLAttribute)(programs || { publishingTracks: [] });
    // Store callback in registry if provided
    const callbackKey = updateCalendarSettings
        ? `__calendarNav_${id}`
        : undefined;
    if (updateCalendarSettings && callbackKey) {
        // Following instruction #3 - minimize globals, use scoped pattern
        if (!window.__calendarNavigationCallbacks) {
            window.__calendarNavigationCallbacks = new Map();
        }
        window.__calendarNavigationCallbacks.set(callbackKey, updateCalendarSettings);
    }
    const html = `<calendar-navigation 
        id="${id}"
        day_string="${day_string ?? Date.now()}"
        calendar_view="${calendar_view}"
        selected_track="${selected_track || ""}"
        tracks='${tracksAsEscapedString}'
        programs='${programsAsEscapedString}'
        view_as_roles='${viewAsRolesAsEscapedString}'
        selected_view_as_role="${selected_view_as_role || ""}"
        show_descriptions="${show_descriptions ? "true" : "false"}"
        show_announcements_and_photo="${show_announcements_and_photo ? "true" : "false"}"
        week_start="${week_start}"
        ${callbackKey ? `callback-key="${callbackKey}"` : ""}
    ></calendar-navigation>`;
    return html;
};
exports.CalendarNavigation = CalendarNavigation;
// Register the web component
(0, web_components_1.registerWebComponent)("calendar-navigation", CalendarNavigationElement);
//# sourceMappingURL=CalendarNavigation.js.map